import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useMemo } from "react";
import { Card, Col, Stack } from "react-bootstrap";
import { Bar } from "react-chartjs-2";
import ErrorResponse from "./ErrorResponse";
import { useResolvedRadioNames } from "./HelperHooks";
import LoadingIndicator from "./LoadingIndicator";
import { getRadioName } from "./HelperFunctions";
import { useState } from "react";
import RATooltip from "./RATooltip";
import ToggleButtonGroup from "react-bootstrap/ToggleButtonGroup";
import ToggleButton from "react-bootstrap/ToggleButton";

ChartJS.register(ArcElement, Tooltip, Legend, annotationPlugin, ChartDataLabels);

export default function SongsOverlapChart({ data, loading, error, moduleName }) {
    const resolvedRadios = useResolvedRadioNames();
    const [grouping, setGrouping] = useState(true);

    const handleGroupingChange = value => {
        setGrouping(value);
    };

    const chartData = useMemo(() => {
        if (data) {
            const libraryCommonPercentage1 = ((data.common_songs_count * 100) / (data.unique_songs_count[0].count + data.common_songs_count)).toFixed(1);
            const libraryCommonPercentage2 = ((data.common_songs_count * 100) / (data.unique_songs_count[1].count + data.common_songs_count)).toFixed(1);
            const libraryUniquePercentage1 = ((data.unique_songs_count[0].count * 100) / (data.unique_songs_count[0].count + data.common_songs_count)).toFixed(
                1
            );
            const libraryUniquePercentage2 = ((data.unique_songs_count[1].count * 100) / (data.unique_songs_count[1].count + data.common_songs_count)).toFixed(
                1
            );
            const rotationCommonPercentage1 = (
                (data.common_songs_spin_count[0].count * 100) /
                (data.unique_songs_spin_count[0].count + data.common_songs_spin_count[0].count)
            ).toFixed(1);
            const rotationCommonPercentage2 = (
                (data.common_songs_spin_count[1].count * 100) /
                (data.unique_songs_spin_count[1].count + data.common_songs_spin_count[1].count)
            ).toFixed(1);
            const rotationUniquePercentage1 = (
                (data.unique_songs_spin_count[0].count * 100) /
                (data.unique_songs_spin_count[0].count + data.common_songs_spin_count[0].count)
            ).toFixed(1);
            const rotationUniquePercentage2 = (
                (data.unique_songs_spin_count[1].count * 100) /
                (data.unique_songs_spin_count[1].count + data.common_songs_spin_count[1].count)
            ).toFixed(1);

            const labels = data.input.radio_ids.map(x => getRadioName(x, resolvedRadios));

            const datasets = grouping
                ? [
                      {
                          label: "Common songs",
                          type: "bar",
                          backgroundColor: ["#396A93", "#396A93"],
                          data: [data.common_songs_count, data.common_songs_count],
                          datalabels: {
                              anchor: "start",
                              align: "end",
                              formatter: function (value, context) {
                                  if (context.dataset.label === "Common songs") {
                                      const commonPercentage = context.dataIndex === 0 ? libraryCommonPercentage1 : libraryCommonPercentage2;
                                      return `${value} (${commonPercentage}%)`;
                                  } else if (context.dataset.label === "Unique songs") {
                                      const uniquePercentage = context.dataIndex === 0 ? libraryUniquePercentage1 : libraryUniquePercentage2;
                                      return `${value} (${uniquePercentage}%)`;
                                  }
                              },
                          },
                      },
                      {
                          label: "Unique songs",
                          type: "bar",
                          backgroundColor: ["#7BA7CC", "#7BA7CC"],
                          data: [data.unique_songs_count[0].count, data.unique_songs_count[1].count],
                          datalabels: {
                              anchor: "center",
                              align: "end",
                              formatter: function (value, context) {
                                  if (context.dataset.label === "Common songs") {
                                      const commonPercentage = context.dataIndex === 0 ? libraryCommonPercentage1 : libraryCommonPercentage2;
                                      return `${value} (${commonPercentage}%)`;
                                  } else if (context.dataset.label === "Unique songs") {
                                      const uniquePercentage = context.dataIndex === 0 ? libraryUniquePercentage1 : libraryUniquePercentage2;
                                      return `${value} (${uniquePercentage}%)`;
                                  }
                              },
                          },
                      },
                  ]
                : [
                      {
                          label: "Common songs spins",
                          type: "bar",
                          backgroundColor: ["#396A93", "#396A93"],
                          data: [data.common_songs_spin_count[0].count, data.common_songs_spin_count[1].count],
                          datalabels: {
                              anchor: "start",
                              align: "end",
                              formatter: function (value, context) {
                                  if (context.dataset.label === "Common songs spins") {
                                      const commonPercentage = context.dataIndex === 0 ? rotationCommonPercentage1 : rotationCommonPercentage2;
                                      return `${value} (${commonPercentage}%)`;
                                  } else if (context.dataset.label === "Unique songs spins") {
                                      const uniquePercentage2 = context.dataIndex === 0 ? rotationUniquePercentage1 : rotationUniquePercentage2;
                                      return `${value} (${uniquePercentage2}%)`;
                                  }
                              },
                          },
                      },
                      {
                          label: "Unique songs spins",
                          type: "bar",
                          backgroundColor: ["#7BA7CC", "#7BA7CC"],
                          data: [data.unique_songs_spin_count[0].count, data.unique_songs_spin_count[1].count],
                          datalabels: {
                              anchor: "center",
                              align: "end",
                              formatter: function (value, context) {
                                  if (context.dataset.label === "Common songs spins") {
                                      const commonPercentage = context.dataIndex === 0 ? rotationCommonPercentage1 : rotationCommonPercentage2;
                                      return `${value} (${commonPercentage}%)`;
                                  } else if (context.dataset.label === "Unique songs spins") {
                                      const uniquePercentage = context.dataIndex === 0 ? rotationUniquePercentage1 : rotationUniquePercentage2;
                                      return `${value} (${uniquePercentage}%)`;
                                  }
                              },
                          },
                      },
                  ];

            return {
                labels,
                datasets,
            };
        }
    }, [data, resolvedRadios, grouping]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        borderRadius: 5,
        pointStyle: "rectRounded",
        indexAxis: "y",
        plugins: {
            legend: { labels: { usePointStyle: true } },
            tooltip: {
                grid: { display: false },
                displayColors: true,
                callbacks: { label: item => `${item.dataset.label}: ${item.raw.toLocaleString()}` },
            },
            datalabels: {
                backgroundColor: context => context.dataset.backgroundColor,
                color: "white",
                font: {
                    weight: "bold",
                    size: 16,
                },
                borderRadius: 5,
                padding: 5,
                formatter: value => (typeof value === "number" ? value.toLocaleString() : null),
            },
        },
        scales: {
            x: {
                ticks: { display: false },
                grid: { display: false },
                stacked: true,
                border: { display: false },
            },
            y: {
                grid: { display: false },
                stacked: true,
            },
        },
        interaction: { mode: "index" },
    };

    return (
        <>
            <Stack direction="horizontal" gap={2}>
                <span>
                    Comparison
                    <RATooltip>
                        <p>
                            <strong>Rotation </strong> - Counts ALL plays for all songs.
                        </p>
                        <p>
                            <strong>Library</strong> - Counts each SONG only once.
                        </p>
                    </RATooltip>
                </span>
                <ToggleButtonGroup type={"radio"} name="grouping" value={grouping} onChange={handleGroupingChange} className="ra-switcher">
                    <ToggleButton id={`${moduleName}-rotation`} variant={"ra-dark-blue-outline"} value={false}>
                        Rotation
                    </ToggleButton>
                    <ToggleButton id={`${moduleName}-library`} className=" text-nowrap" variant={"ra-dark-blue-outline"} value={true}>
                        Library
                    </ToggleButton>
                </ToggleButtonGroup>
            </Stack>
            <Card>
                <Card.Header className="text-center">Songs Overlap</Card.Header>
                <Card.Body className="p-1 overflow-auto ">
                    {loading ? (
                        <LoadingIndicator width={75} />
                    ) : error ? (
                        <ErrorResponse errorCode={error} />
                    ) : (
                        data && (
                            <Col className="overlap-chart">
                                <Bar key={grouping} data={chartData} options={chartOptions} />
                            </Col>
                        )
                    )}
                </Card.Body>
            </Card>
        </>
    );
}
